import { useAuthStore } from '@/stores/auth'
import WritingHabitsService from '@/services/writing-habits'
import {
  createTypingAccumulator,
  ingestClosedWord,
  ingestSentenceBreak,
  resetTypingAccumulator,
  topTerms,
  type TypingAccumulator,
} from '@/utils/writingHabitsTerms'
import type { WritingHabitsBatchPayload } from '@/types/writing-habits'
import { onSessionEndDiscard } from '@/utils/sessionLifecycle'

export const WRITING_HABITS_PENDING_STORAGE_KEY = 'escribonauta_writing_habits_pending_batches'

const MAX_TERMS_PER_BATCH = 500
const MAX_PENDING_BATCHES = 5
const FLUSH_INTERVAL_MS = 150_000
const WORDS_PER_FLUSH = 300

let counts = new Map<string, number>()
let wordsInBatch = 0
const accumulator: TypingAccumulator = createTypingAccumulator()
let flushTimer: ReturnType<typeof setInterval> | null = null
let sending = false
let started = false
let listenersBound = false
let sessionEndBound = false

function authSnapshot(): { allowed: boolean; userId: string } {
  try {
    const auth = useAuthStore()
    const userId = (auth.currentUser as { userId?: string } | null)?.userId || ''
    const allowed =
      auth.userType === 'registered' && !auth.isImpersonating && !!userId
    return { allowed, userId }
  } catch {
    return { allowed: false, userId: '' }
  }
}

function newClientBatchId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `wh-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function isStoredBatch(item: unknown): item is WritingHabitsBatchPayload {
  return (
    !!item &&
    typeof item === 'object' &&
    typeof (item as WritingHabitsBatchPayload).clientBatchId === 'string' &&
    typeof (item as WritingHabitsBatchPayload).words === 'number' &&
    typeof (item as WritingHabitsBatchPayload).userId === 'string' &&
    Array.isArray((item as WritingHabitsBatchPayload).terms)
  )
}

function readPending(userId: string): WritingHabitsBatchPayload[] {
  if (typeof localStorage === 'undefined') return []
  try {
    const raw = localStorage.getItem(WRITING_HABITS_PENDING_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isStoredBatch).filter((item) => item.userId === userId)
  } catch {
    return []
  }
}

function writePending(batches: WritingHabitsBatchPayload[]) {
  if (typeof localStorage === 'undefined') return
  const trimmed = batches.slice(-MAX_PENDING_BATCHES)
  localStorage.setItem(WRITING_HABITS_PENDING_STORAGE_KEY, JSON.stringify(trimmed))
}

function snapshotCurrentBatch(userId: string): WritingHabitsBatchPayload | null {
  if (wordsInBatch <= 0 && counts.size === 0) return null
  return {
    terms: topTerms(counts, MAX_TERMS_PER_BATCH),
    words: wordsInBatch,
    clientBatchId: newClientBatchId(),
    userId,
  }
}

function resetCurrentBatch() {
  counts = new Map()
  wordsInBatch = 0
  resetTypingAccumulator(accumulator)
}

function enqueueBatch(payload: WritingHabitsBatchPayload) {
  const pending = readPending(payload.userId)
  if (pending.some((item) => item.clientBatchId === payload.clientBatchId)) return
  pending.push(payload)
  writePending(pending)
}

function persistCurrentBatch(userId: string): WritingHabitsBatchPayload | null {
  const payload = snapshotCurrentBatch(userId)
  if (!payload) return null
  enqueueBatch(payload)
  resetCurrentBatch()
  return payload
}

function httpStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object' || !('response' in error)) return undefined
  return (error as { response?: { status?: number } }).response?.status
}

function shouldDropStatus(status: number | undefined): boolean {
  if (status === undefined) return false
  if (status === 408 || status === 429) return false
  return status >= 400 && status < 500
}

async function sendPayload(payload: WritingHabitsBatchPayload): Promise<boolean> {
  const { allowed, userId } = authSnapshot()
  if (!allowed || payload.userId !== userId) {
    return true
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return false
  }
  try {
    await WritingHabitsService.sendBatch(payload)
    return true
  } catch (error) {
    if (shouldDropStatus(httpStatus(error))) {
      return true
    }
    return false
  }
}

async function sendPendingBatches(userId: string) {
  const pending = readPending(userId)
  if (pending.length === 0) return
  const results = await Promise.all(pending.map((payload) => sendPayload(payload)))
  writePending(pending.filter((_, index) => !results[index]))
}

export async function flushWritingHabitsBatch() {
  const { allowed, userId } = authSnapshot()
  if (!allowed) {
    resetCurrentBatch()
    return
  }
  persistCurrentBatch(userId)
  if (sending) return
  sending = true
  try {
    await sendPendingBatches(userId)
  } finally {
    sending = false
  }
}

function persistAndKeepalive() {
  const { allowed, userId } = authSnapshot()
  if (!allowed) {
    resetCurrentBatch()
    return
  }
  persistCurrentBatch(userId)
  const pending = readPending(userId)
  pending.forEach((payload) => {
    void WritingHabitsService.sendBatchKeepalive(payload)
      .then((ok) => {
        if (!ok) return
        writePending(readPending(userId).filter((item) => item.clientBatchId !== payload.clientBatchId))
      })
      .catch(() => {})
  })
}

export function recordClosedWritingHabitsWord(rawWord: string) {
  if (!rawWord) return
  const { allowed } = authSnapshot()
  if (!allowed) return
  const wordsRef = { n: 0 }
  ingestClosedWord(accumulator, rawWord, counts, wordsRef)
  wordsInBatch += wordsRef.n
  if (wordsInBatch >= WORDS_PER_FLUSH) {
    void flushWritingHabitsBatch()
  }
}

export function recordWritingHabitsSentenceBreak() {
  ingestSentenceBreak(accumulator)
}

export function resetWritingHabitsTypingContext() {
  resetTypingAccumulator(accumulator)
}

function onOnline() {
  void flushWritingHabitsBatch()
}

function onPageHide() {
  persistAndKeepalive()
}

export function startWritingHabitsCollector() {
  if (started) return
  started = true
  if (!sessionEndBound) {
    sessionEndBound = true
    onSessionEndDiscard(() => {
      resetCurrentBatch()
    })
  }
  if (!listenersBound && typeof window !== 'undefined') {
    listenersBound = true
    window.addEventListener('online', onOnline)
    window.addEventListener('pagehide', onPageHide)
  }
  if (!flushTimer) {
    flushTimer = setInterval(() => {
      void flushWritingHabitsBatch()
    }, FLUSH_INTERVAL_MS)
  }
  void flushWritingHabitsBatch()
}
